/**
 * MarkupEditor (FILES-EDIT-01 / MARKUP-02, Seth 2026-09-28: "Will there be an
 * easy way to download, edit, markup, rename, etc from the office?").
 *
 * Draw on a photo, or on one page of a PDF (plans), and save a MARKED-UP COPY
 * next to the original — the original never changes. The copy keeps the
 * original's job / call / type / crew switch, so a marked plan page shows on
 * the crew's phone like any photo.
 *
 * Competitor frame: ServiceTitan's office Annotation Manager (images + PDFs,
 * saved as versions) and Buildertrend's markups (shapes, colors, stamps) —
 * adopted: pen, arrow, box, circle, text, colors, undo. Rejected for now:
 * measuring tools and stamps (plans are marked "furnace here", not measured).
 *
 * MARKUP-03 (2026-09-28): the same editor on the phone (touch drawing, a
 * toolbar that fits a phone), and a blank Sketch — grid paper for the house's
 * outline with the wall lengths, or where the new unit goes — saved as a
 * Sketch file on the job (or the call, before there's a job).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  ArrowUpRight, Check, ChevronLeft, ChevronRight, Circle, Hand, Loader2, Minus, Pencil, Plus, Square, Trash2, Type, Undo2, X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { BUCKET, isPdfFile, uploadOneFile, type PegasusFile, type UploadTarget } from "./filesCore";
import { openPdf, renderPdfPage, type PdfDoc } from "./pdfjs";

type Tool = "pen" | "arrow" | "box" | "circle" | "text" | "hand";
type Pt = [number, number];
type Shape =
  | { t: "pen"; c: string; w: number; pts: Pt[] }
  | { t: "arrow" | "box" | "circle"; c: string; w: number; a: Pt; b: Pt }
  | { t: "text"; c: string; w: number; at: Pt; text: string };

const TOOLS: { k: Tool; label: string; Icon: typeof Pencil }[] = [
  { k: "pen", label: "Draw", Icon: Pencil },
  { k: "arrow", label: "Arrow", Icon: ArrowUpRight },
  { k: "box", label: "Box", Icon: Square },
  { k: "circle", label: "Circle", Icon: Circle },
  { k: "text", label: "Text", Icon: Type },
  { k: "hand", label: "Move around", Icon: Hand },
];
const COLORS = [
  { c: "#e11d48", label: "Red" },
  { c: "#facc15", label: "Yellow" },
  { c: "#2563eb", label: "Blue" },
  { c: "#16a34a", label: "Green" },
  { c: "#0f172a", label: "Black" },
  { c: "#ffffff", label: "White" },
];
const SIZES = [{ k: 1, label: "Thin" }, { k: 2, label: "Medium" }, { k: 3.5, label: "Thick" }];
const MAX_SIDE = 2400;

/** A dark outline for light colors, a light one for dark colors — text stays readable on any background. */
const halo = (c: string) => (["#facc15", "#ffffff"].includes(c) ? "rgba(15,23,42,0.85)" : "rgba(255,255,255,0.9)");

function drawShapes(ctx: CanvasRenderingContext2D, shapes: Shape[], unit: number) {
  for (const s of shapes) {
    ctx.save();
    ctx.strokeStyle = s.c;
    ctx.fillStyle = s.c;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const lw = Math.max(1.5, s.w * unit * 1.6);
    ctx.lineWidth = lw;
    if (s.t === "pen") {
      ctx.beginPath();
      s.pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      if (s.pts.length === 1) ctx.lineTo(s.pts[0][0] + 0.1, s.pts[0][1]);
      ctx.stroke();
    } else if (s.t === "box") {
      const x = Math.min(s.a[0], s.b[0]);
      const y = Math.min(s.a[1], s.b[1]);
      ctx.strokeRect(x, y, Math.abs(s.b[0] - s.a[0]), Math.abs(s.b[1] - s.a[1]));
    } else if (s.t === "circle") {
      const cx = (s.a[0] + s.b[0]) / 2;
      const cy = (s.a[1] + s.b[1]) / 2;
      ctx.beginPath();
      ctx.ellipse(cx, cy, Math.max(1, Math.abs(s.b[0] - s.a[0]) / 2), Math.max(1, Math.abs(s.b[1] - s.a[1]) / 2), 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (s.t === "arrow") {
      const [x1, y1] = s.a;
      const [x2, y2] = s.b;
      const ang = Math.atan2(y2 - y1, x2 - x1);
      const head = Math.max(lw * 3.2, unit * 6);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2 - Math.cos(ang) * head * 0.6, y2 - Math.sin(ang) * head * 0.6);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - head * Math.cos(ang - Math.PI / 7), y2 - head * Math.sin(ang - Math.PI / 7));
      ctx.lineTo(x2 - head * Math.cos(ang + Math.PI / 7), y2 - head * Math.sin(ang + Math.PI / 7));
      ctx.closePath();
      ctx.fill();
    } else if (s.t === "text") {
      const px = Math.round((8 + 4 * s.w) * unit);
      ctx.font = `700 ${px}px Inter, "Segoe UI", system-ui, sans-serif`;
      ctx.textBaseline = "top";
      ctx.lineWidth = Math.max(2, px * 0.16);
      ctx.strokeStyle = halo(s.c);
      s.text.split("\n").forEach((line, i) => {
        ctx.strokeText(line, s.at[0], s.at[1] + i * px * 1.15);
        ctx.fillText(line, s.at[0], s.at[1] + i * px * 1.15);
      });
    }
    ctx.restore();
  }
}

/** A blank sketch page: grid paper, a darker line every 5 squares. */
function gridPaper(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d");
  if (!ctx) return c;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  const step = 40;
  for (let x = 0, i = 0; x <= w; x += step, i++) {
    ctx.strokeStyle = i % 5 === 0 ? "#cbd5e1" : "#e2e8f0";
    ctx.lineWidth = i % 5 === 0 ? 1.5 : 1;
    ctx.beginPath(); ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, h); ctx.stroke();
  }
  for (let y = 0, i = 0; y <= h; y += step, i++) {
    ctx.strokeStyle = i % 5 === 0 ? "#cbd5e1" : "#e2e8f0";
    ctx.lineWidth = i % 5 === 0 ? 1.5 : 1;
    ctx.beginPath(); ctx.moveTo(0, y + 0.5); ctx.lineTo(w, y + 0.5); ctx.stroke();
  }
  return c;
}

export function MarkupEditor({
  file,
  sketch,
  origin = "office",
  onClose,
  onSaved,
}: {
  /** Who's drawing — the phone saves its copies as the tech's. */
  origin?: "office" | "tech";
  /** The photo or PDF to mark up — or leave it out and pass `sketch` for a blank page. */
  file?: PegasusFile | null;
  /** MARKUP-03: a blank sketch saved to this job / call. */
  sketch?: { target: UploadTarget; title?: string };
  onClose: () => void;
  /** The marked-up copy (or the sketch), once it's saved. */
  onSaved: (copy: PegasusFile) => void;
}) {
  const isPdf = !!file && isPdfFile(file);
  const blank = !file;
  const baseRef = useRef<HTMLCanvasElement>(null);
  const overRef = useRef<HTMLCanvasElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pdfRef = useRef<PdfDoc | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [tool, setTool] = useState<Tool>("pen");
  const [color, setColor] = useState(COLORS[0].c);
  const [size, setSize] = useState(2);
  const [shapes, setShapes] = useState<Shape[]>([]);
  // The shape being drawn lives in a ref as well: pointer moves arrive faster
  // than React renders, and every point must land on the stroke.
  const [draft, setDraftState] = useState<Shape | null>(null);
  const draftRef = useRef<Shape | null>(null);
  const setDraft = (v: Shape | null) => { draftRef.current = v; setDraftState(v); };
  const [typing, setTypingState] = useState<{ at: Pt; css: Pt; value: string } | null>(null);
  // The open text box lives in a ref too: a click elsewhere fires the canvas's
  // pointerdown AND the box's blur — the ref makes the second one a no-op.
  const typingRef = useRef<{ at: Pt; css: Pt; value: string } | null>(null);
  const setTyping = (v: { at: Pt; css: Pt; value: string } | null) => { typingRef.current = v; setTypingState(v); };
  const [fit, setFit] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<null | { why: "close" } | { why: "page"; to: number }>(null);
  const pan = useRef<{ x: number; y: number; l: number; t: number } | null>(null);
  const unit = dims ? Math.max(dims.w, dims.h) / 500 : 1;
  const dirty = shapes.length > 0;

  // Put a picture on the base canvas (the photo, or a PDF page), capped so a
  // 48-megapixel phone photo doesn't choke the browser.
  const paint = useCallback((src: CanvasImageSource, w: number, h: number) => {
    const k = Math.min(1, MAX_SIDE / Math.max(w, h));
    const W = Math.round(w * k);
    const H = Math.round(h * k);
    for (const c of [baseRef.current, overRef.current]) {
      if (!c) continue;
      c.width = W;
      c.height = H;
    }
    const ctx = baseRef.current?.getContext("2d");
    if (ctx) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, W, H);
      ctx.drawImage(src, 0, 0, W, H);
    }
    setDims({ w: W, h: H });
    setZoom(1);
  }, []);

  const showPage = useCallback(async (n: number) => {
    const doc = pdfRef.current;
    if (!doc) return;
    setLoading(true);
    try {
      const c = await renderPdfPage(doc, n, 2000);
      paint(c, c.width, c.height);
      setPage(n);
      setShapes([]);
    } finally {
      setLoading(false);
    }
  }, [paint]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    if (!file) {
      // A sketch: grid paper, shaped to the screen it's drawn on. The dialog mounts its canvases a beat after
      // this runs, so wait for them.
      const portrait = window.innerHeight > window.innerWidth;
      const paper = portrait ? gridPaper(1200, 1600) : gridPaper(1600, 1200);
      let tries = 0;
      const go = () => {
        if (cancelled) return;
        if (!baseRef.current || !overRef.current) {
          if (tries++ < 100) window.setTimeout(go, 20);
          return;
        }
        paint(paper, paper.width, paper.height);
        setLoading(false);
      };
      go();
      return () => { cancelled = true; };
    }
    (async () => {
      const { data: blob, error } = await supabase.storage.from(BUCKET).download(file.storage_path);
      if (error || !blob) throw error ?? new Error("download");
      if (cancelled) return;
      if (isPdf) {
        const doc = await openPdf(new Uint8Array(await blob.arrayBuffer()));
        if (cancelled) { void doc.destroy(); return; }
        pdfRef.current = doc;
        setPages(doc.numPages);
        await showPage(1);
      } else {
        const bmp = await createImageBitmap(blob, { imageOrientation: "from-image" });
        if (cancelled) return;
        paint(bmp, bmp.width, bmp.height);
        setLoading(false);
      }
    })().catch(() => {
      if (cancelled) return;
      setErr(isPdf ? "Couldn't open this PDF to mark it up." : "This photo can't be marked up in the browser (HEIC or TIFF) — download it instead.");
      setLoading(false);
    });
    return () => {
      cancelled = true;
      void pdfRef.current?.destroy();
      pdfRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.storage_path, isPdf, paint, showPage]);

  // Fit the picture to the window; recompute when the window resizes.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !dims) return;
    const measure = () => setFit(Math.min(1, (el.clientWidth - 32) / dims.w, (el.clientHeight - 32) / dims.h));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [dims]);

  // Redraw the marks whenever they change.
  useEffect(() => {
    const c = overRef.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    drawShapes(ctx, draft ? [...shapes, draft] : shapes, unit);
  }, [shapes, draft, unit, dims]);

  const undo = useCallback(() => setShapes((s) => s.slice(0, -1)), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !typing) { e.preventDefault(); undo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, typing]);

  const toImage = (e: RPointerEvent<HTMLCanvasElement>): { p: Pt; css: Pt } => {
    const c = e.currentTarget;
    const r = c.getBoundingClientRect();
    return {
      p: [((e.clientX - r.left) / r.width) * c.width, ((e.clientY - r.top) / r.height) * c.height],
      css: [e.clientX - r.left, e.clientY - r.top],
    };
  };

  const commitText = () => {
    const t = typingRef.current;
    if (!t) return;
    if (t.value.trim()) setShapes((s) => [...s, { t: "text", c: color, w: size, at: t.at, text: t.value.trim() }]);
    setTyping(null);
  };

  const onDown = (e: RPointerEvent<HTMLCanvasElement>) => {
    if (!dims || loading) return;
    if (tool === "hand") {
      const sc = scrollRef.current;
      if (sc) pan.current = { x: e.clientX, y: e.clientY, l: sc.scrollLeft, t: sc.scrollTop };
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    const { p, css } = toImage(e);
    if (tool === "text") {
      // A click while a box is open finishes that text; the next click starts a new one.
      if (typingRef.current) { commitText(); return; }
      setTyping({ at: p, css, value: "" });
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    setDraft(tool === "pen" ? { t: "pen", c: color, w: size, pts: [p] } : { t: tool, c: color, w: size, a: p, b: p });
  };
  const onMove = (e: RPointerEvent<HTMLCanvasElement>) => {
    if (tool === "hand") {
      const sc = scrollRef.current;
      if (sc && pan.current) {
        sc.scrollLeft = pan.current.l - (e.clientX - pan.current.x);
        sc.scrollTop = pan.current.t - (e.clientY - pan.current.y);
      }
      return;
    }
    const d = draftRef.current;
    if (!d) return;
    const { p } = toImage(e);
    setDraft(d.t === "pen" ? { ...d, pts: [...d.pts, p] } : d.t === "text" ? d : { ...d, b: p });
  };
  const onUp = (e?: RPointerEvent<HTMLCanvasElement>) => {
    pan.current = null;
    const d0 = draftRef.current;
    if (!d0) return;
    // The last point counts (a quick flick can end before a move arrives).
    const d = e && d0.t !== "pen" && d0.t !== "text" ? { ...d0, b: toImage(e).p } : d0;
    const tiny = d.t !== "pen" && d.t !== "text" && Math.hypot(d.b[0] - d.a[0], d.b[1] - d.a[1]) < unit * 2;
    if (!tiny) setShapes((s) => [...s, d]);
    setDraft(null);
  };

  const askClose = () => { if (dirty && !saving) setConfirm({ why: "close" }); else onClose(); };
  const goPage = (n: number) => {
    if (n < 1 || n > pages || n === page) return;
    if (dirty) setConfirm({ why: "page", to: n }); else void showPage(n);
  };

  const save = async () => {
    const base = baseRef.current;
    if (!base || !dims || !dirty) return;
    setSaving(true);
    setErr(null);
    try {
      const out = document.createElement("canvas");
      out.width = dims.w;
      out.height = dims.h;
      const ctx = out.getContext("2d");
      if (!ctx) throw new Error("canvas");
      ctx.drawImage(base, 0, 0);
      drawShapes(ctx, shapes, unit);
      // Plans are line drawings — PNG keeps them crisp; photos stay JPEG.
      const type = isPdf || blank ? "image/png" : "image/jpeg";
      const blob = await new Promise<Blob | null>((res) => out.toBlob(res, type, 0.9));
      if (!blob) throw new Error("image");
      const { data: u } = await supabase.auth.getUser();
      const me = (u?.user?.email ?? "").split("@")[0];
      if (!file) {
        const t = sketch?.target;
        if (!t) throw new Error("no target");
        const day = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
        const sk = await uploadOneFile({
          ...t, doc_tag: "sketch", sort: false,
          caption: `${sketch?.title ?? "Sketch"}${me ? ` by ${me}` : ""}`,
        }, new File([blob], `${sketch?.title ?? "Sketch"} ${day}.png`, { type }));
        setShapes([]);
        onSaved(sk);
        return;
      }
      const stem = file.filename.replace(/\.[^.]+$/, "");
      const name = isPdf ? `${stem} p${page} (marked up).png` : `${stem} (marked up).jpg`;
      const copy = await uploadOneFile({
        custno: file.custno, locno: file.locno, dispatch: file.dispatch, job_id: file.job_id,
        doc_tag: file.doc_tag, crew_visible: file.crew_visible, origin, markup_of: file.id,
        caption: `Marked up${me ? ` by ${me}` : ""}${isPdf ? ` — page ${page} of ${file.filename}` : ""}`,
        sort: !file.doc_tag,
      }, new File([blob], name, { type }));
      setShapes([]);
      onSaved(copy);
    } catch {
      setErr(blank ? "Couldn't save the sketch — check the signal and try again." : "Couldn't save the marked-up copy — try again.");
    } finally {
      setSaving(false);
    }
  };

  const scale = fit * zoom;
  const cssW = dims ? Math.round(dims.w * scale) : 0;
  const cssH = dims ? Math.round(dims.h * scale) : 0;
  const btn = "inline-flex h-9 items-center justify-center gap-1.5 rounded-lg px-2.5 text-sm font-semibold transition-colors";

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) askClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-slate-950/90" />
        <Dialog.Content
          className="fixed inset-0 z-[60] flex flex-col text-white outline-none"
          onEscapeKeyDown={(e) => { if (typing) { e.preventDefault(); setTyping(null); } else if (dirty) { e.preventDefault(); setConfirm({ why: "close" }); } }}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Dialog.Title className="sr-only">{file ? `Mark up ${file.filename}` : sketch?.title ?? "Sketch"}</Dialog.Title>
          <Dialog.Description className="sr-only">{blank ? "Draw on the grid, then save the sketch to the job." : "Draw on the picture, then save a marked-up copy. The original stays as it is."}</Dialog.Description>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 border-b border-white/10 bg-slate-900/95 px-3 py-2">
            <button type="button" onClick={askClose} className={cn(btn, "text-slate-300 hover:bg-white/10 hover:text-white")} aria-label="Close">
              <X className="h-5 w-5" />
            </button>
            <div className="mr-2 hidden min-w-0 max-w-[16rem] truncate text-sm font-semibold text-slate-200 sm:block" title={file?.filename}>
              {file ? file.filename : sketch?.title ?? "Sketch"}
            </div>
            <div className="flex items-center gap-0.5 rounded-xl bg-white/5 p-0.5" role="toolbar" aria-label="Tools">
              {TOOLS.map(({ k, label, Icon }) => (
                <button key={k} type="button" onClick={() => { commitText(); setTool(k); }} aria-label={label} aria-pressed={tool === k} title={label}
                  className={cn(btn, "w-9 px-0", tool === k ? "bg-white text-slate-900" : "text-slate-300 hover:bg-white/10 hover:text-white")}>
                  <Icon className="h-[18px] w-[18px]" />
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 rounded-xl bg-white/5 px-1.5 py-1" role="radiogroup" aria-label="Color">
              {COLORS.map(({ c, label }) => (
                <button key={c} type="button" role="radio" aria-checked={color === c} aria-label={label} title={label} onClick={() => setColor(c)}
                  className={cn("h-6 w-6 rounded-full ring-offset-2 ring-offset-slate-900 transition-shadow", color === c ? "ring-2 ring-white" : "ring-1 ring-white/30")}
                  style={{ background: c }} />
              ))}
            </div>
            <div className="flex items-center gap-0.5 rounded-xl bg-white/5 p-0.5" role="radiogroup" aria-label="Line size">
              {SIZES.map(({ k, label }) => (
                <button key={k} type="button" role="radio" aria-checked={size === k} aria-label={label} title={label} onClick={() => setSize(k)}
                  className={cn(btn, "w-9 px-0", size === k ? "bg-white text-slate-900" : "text-slate-300 hover:bg-white/10")}>
                  <span className="block rounded-full bg-current" style={{ width: 4 + k * 3, height: 4 + k * 3 }} />
                </button>
              ))}
            </div>
            <button type="button" onClick={undo} disabled={!dirty} className={cn(btn, "text-slate-200 hover:bg-white/10 disabled:opacity-40")} title="Undo (Ctrl+Z)" aria-label="Undo">
              <Undo2 className="h-4 w-4" /> <span className="hidden sm:inline">Undo</span>
            </button>
            <button type="button" onClick={() => setShapes([])} disabled={!dirty} className={cn(btn, "text-slate-200 hover:bg-white/10 disabled:opacity-40")} aria-label="Clear">
              <Trash2 className="h-4 w-4" /> <span className="hidden sm:inline">Clear</span>
            </button>
            <span className="ml-auto flex items-center gap-1">
              <button type="button" onClick={() => setZoom((z) => Math.max(0.5, +(z / 1.25).toFixed(2)))} className={cn(btn, "hidden w-9 px-0 text-slate-200 hover:bg-white/10 sm:inline-flex")} aria-label="Zoom out"><Minus className="h-4 w-4" /></button>
              <button type="button" onClick={() => setZoom(1)} className={cn(btn, "hidden min-w-[3.5rem] text-slate-200 hover:bg-white/10 tabular-nums sm:inline-flex")} title="Fit to the window">{Math.round(scale * 100)}%</button>
              <button type="button" onClick={() => setZoom((z) => Math.min(6, +(z * 1.25).toFixed(2)))} className={cn(btn, "hidden w-9 px-0 text-slate-200 hover:bg-white/10 sm:inline-flex")} aria-label="Zoom in"><Plus className="h-4 w-4" /></button>
              <button type="button" onClick={() => void save()} disabled={!dirty || saving}
                className={cn(btn, "ml-2 bg-brand-500 px-3.5 text-white hover:bg-brand-600 disabled:bg-white/10 disabled:text-slate-400")}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {blank ? "Save the Sketch" : <><span className="sm:hidden">Save a Copy</span><span className="hidden sm:inline">Save a Marked-Up Copy</span></>}
              </button>
            </span>
          </div>

          {(confirm || err) && (
            <div className="flex flex-wrap items-center gap-3 bg-amber-500/15 px-4 py-2 text-sm text-amber-100">
              {err && !confirm && <span>{err}</span>}
              {confirm && (
                <>
                  <span>{confirm.why === "close" ? (blank ? "Leave without saving the sketch?" : "Leave without saving your marks?") : "Go to another page? The marks on this page aren't saved."}</span>
                  <button type="button" onClick={() => setConfirm(null)} className="rounded-lg bg-white/10 px-3 py-1 font-semibold hover:bg-white/20">Keep Marking</button>
                  <button type="button"
                    onClick={() => { const c = confirm; setConfirm(null); setShapes([]); if (c.why === "close") onClose(); else void showPage(c.to); }}
                    className="rounded-lg bg-rose-600 px-3 py-1 font-semibold text-white hover:bg-rose-700">
                    Discard the Marks
                  </button>
                </>
              )}
            </div>
          )}

          {/* The picture */}
          <div ref={scrollRef} className="relative min-h-0 flex-1 overflow-auto">
            <div className="flex min-h-full min-w-full items-center justify-center p-4" style={{ width: cssW ? cssW + 32 : undefined }}>
              <div className="relative shadow-2xl" style={{ width: cssW || undefined, height: cssH || undefined }}>
                <canvas ref={baseRef} className="absolute inset-0 h-full w-full bg-white" />
                <canvas
                  ref={overRef}
                  onPointerDown={onDown}
                  onPointerMove={onMove}
                  onPointerUp={onUp}
                  onPointerCancel={() => onUp()}
                  className={cn("absolute inset-0 h-full w-full touch-none", tool === "hand" ? "cursor-grab active:cursor-grabbing" : tool === "text" ? "cursor-text" : "cursor-crosshair")}
                />
                {typing && (
                  <textarea
                    autoFocus
                    rows={1}
                    value={typing.value}
                    onChange={(e) => setTyping({ ...typing, value: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commitText(); }
                      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setTyping(null); }
                    }}
                    onBlur={commitText}
                    placeholder="Type, then Enter"
                    className="absolute min-w-[10rem] resize-none rounded-md border-2 bg-white/95 px-2 py-1 text-base font-bold text-slate-900 shadow-lg outline-none"
                    style={{ left: typing.css[0], top: typing.css[1], borderColor: color }}
                  />
                )}
              </div>
            </div>
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-white/70" />
              </div>
            )}
          </div>

          {isPdf && pages > 1 && (
            <div className="flex items-center justify-center gap-3 border-t border-white/10 bg-slate-900/95 py-2 text-sm">
              <button type="button" onClick={() => goPage(page - 1)} disabled={page <= 1 || loading} className={cn(btn, "text-slate-200 hover:bg-white/10 disabled:opacity-40")}>
                <ChevronLeft className="h-4 w-4" /> Previous
              </button>
              <span className="tabular-nums text-slate-300">Page {page} of {pages}</span>
              <button type="button" onClick={() => goPage(page + 1)} disabled={page >= pages || loading} className={cn(btn, "text-slate-200 hover:bg-white/10 disabled:opacity-40")}>
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
